/**
 * Taxonomy helpers — pure functions + the API client used by FilterContext.
 *
 * Themes and property types are returned by the backend as a single
 * `taxonomies` array with a `kind` discriminator (mirrors migration 018).
 * Splitting / lookup / color resolution all live here so the context and
 * the chip components stay focused on rendering and state.
 */

import axios from "axios";
import config from "../config";

export const TAXONOMY_KIND = Object.freeze({
  THEME: "theme",
  PROPERTY_TYPE: "property_type",
  COMMUNITY: "community",
});

/**
 * KIND_META — single source of truth for the taxonomy kinds, their
 * display labels, and whether they carry per-story ranking. Every
 * kind-aware surface (the FilterBar segmented toggle, the FilterContext
 * split, AdminTaxonomies sections, the wizard, the side panel) iterates
 * this list instead of hardcoding the kinds. Adding a fourth kind is a
 * one-line change here (R2).
 *
 * Order is the FilterBar segmented-toggle order, top → bottom, locked by
 * the June 10 2026 client screenshot: Theme, Property type, Communities.
 * Labels match the screenshot exactly — "Communities" is intentionally
 * plural per the client; the other two are singular.
 *
 * `ranked: true` means the kind carries Primary / Secondary / Tertiary
 * ranks per story and drives marker color by rank. Client July 2026
 * feedback (#7): Property Type and Communities now use the SAME ranked
 * Primary/Secondary/Tertiary picker as Themes, so all three kinds are
 * ranked. The lowest-rank selected chip within the active lens wins the
 * marker color (see pickPrimaryChipColor).
 */
export const KIND_META = Object.freeze([
  { key: TAXONOMY_KIND.THEME, label: "Theme", ranked: true },
  { key: TAXONOMY_KIND.PROPERTY_TYPE, label: "Property type", ranked: true },
  { key: TAXONOMY_KIND.COMMUNITY, label: "Communities", ranked: true },
]);

/** Ordered list of just the kind keys (segmented-toggle order). */
export const KIND_ORDER = Object.freeze(KIND_META.map((m) => m.key));

/** Map of kind key → display label, derived from KIND_META. */
export const KIND_LABELS = Object.freeze(
  KIND_META.reduce((acc, m) => {
    acc[m.key] = m.label;
    return acc;
  }, {}),
);

/** True when the kind carries Primary/Secondary/Tertiary ranks (themes). */
export function kindIsRanked(kind) {
  const meta = KIND_META.find((m) => m.key === kind);
  return !!(meta && meta.ranked);
}

/**
 * Fetch the full chip catalogue for one project.
 *
 * Returns `[]` (never throws) on network or schema errors so the
 * FilterContext can degrade gracefully — a failed fetch should hide the
 * filter UI, not crash the map.
 */
export async function fetchTaxonomies(projectId) {
  if (!projectId) return [];
  try {
    const res = await axios.post(config.getApiUrl("taxonomyManager.php"), {
      action: "list",
      project_id: projectId,
    });
    if (res.data && res.data.success && Array.isArray(res.data.taxonomies)) {
      return res.data.taxonomies;
    }
    return [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("fetchTaxonomies failed:", err);
    return [];
  }
}

/**
 * Is this chip a catch-all ("Other" / "Unspecified") bucket?
 *
 * The `is_catch_all` flag (migration 025) is AUTHORITATIVE WHEN PRESENT;
 * the name regex is only a fallback for pre-migration / deploy-lag
 * payloads where the backend hasn't sent the field yet. This ordering
 * makes the column win once it ships: an admin un-flagging a chip named
 * "Other", or flagging a "Unknown" bucket, overrides the name heuristic.
 *
 * Catch-all chips are conceptual fallback buckets ("doesn't fit /
 * unassigned"), so on the MAP they render greyscale rather than their
 * catalogue color (see pickPrimaryChipColor). Chip PILLS keep their
 * catalogue color — this is a map-only rule.
 */
export function isCatchAllTaxonomy(t) {
  if (t && t.is_catch_all != null) {
    return t.is_catch_all === 1 || t.is_catch_all === true;
  }
  return /^(other|unspecified)$/i.test(((t && t.name) || "").trim());
}

/**
 * Sort comparator that orders taxonomies alphabetically but always pins
 * catch-all chips ("Other" / "Unspecified") to the end. We do this
 * because both are conceptual fallback buckets — readers scan the list
 * expecting the meaningful categories first and the "I don't know /
 * doesn't fit" bucket last. The v021 catalogue introduced "Unspecified"
 * alongside the legacy "Other" so we treat them identically here.
 *
 * Catch-all detection reuses `isCatchAllTaxonomy` (flag-authoritative-
 * when-present + name fallback) so the sort and the map-color rule can
 * never drift.
 *
 * If both chips are catch-alls they still sort alphabetically between
 * themselves (Other < Unspecified), which keeps the relative order
 * stable when a project's admin adds both.
 *
 * Locale-aware compare is used for the alpha portion so accented
 * names sort consistently with the rest of the UI.
 */
export function compareTaxonomiesWithOtherLast(a, b) {
  const aName = (a?.name || "").trim();
  const bName = (b?.name || "").trim();
  const aIsCatchAll = isCatchAllTaxonomy(a);
  const bIsCatchAll = isCatchAllTaxonomy(b);
  if (aIsCatchAll && !bIsCatchAll) return 1;
  if (!aIsCatchAll && bIsCatchAll) return -1;
  return aName.localeCompare(bName);
}

/**
 * Ranked-chip transforms (shared by the wizard reducer AND the
 * ContributionComp create/edit picker — Codex eng-review: one source of
 * truth for rank semantics so a local-state copy can't diverge).
 *
 * These are KIND-AGNOSTIC {1,2,3}-map operations. They were introduced
 * for Themes, but client July 2026 feedback (#7) extended the ranked
 * Primary/Secondary/Tertiary model to Property Type and Communities too,
 * so the same helpers now drive all three kinds' rank maps. The
 * `setThemeAtRank` / `clearThemeAtRank` names are historical.
 *
 * `ranks` is the fixed-shape `{ 1: id|null, 2: id|null, 3: id|null }` map
 * the picker renders. Both helpers are pure and return a NEW map.
 */

/**
 * Assign `id` to rank slot `rank` (1-3). If `id` already lives in a
 * different slot it's removed from there first, so the same chip can never
 * hold two ranks (which would corrupt the marker-color algorithm). Pass
 * `id = null` to clear the slot in place (no compaction — use
 * clearThemeAtRank for that).
 */
export function setThemeAtRank(ranks, rank, id) {
  const r = Number(rank);
  if (!Number.isFinite(r) || r < 1 || r > 3) return ranks;
  const nid = id == null ? null : Number(id);
  if (nid != null && (!Number.isFinite(nid) || nid <= 0)) return ranks;
  const next = {
    1: ranks?.[1] ?? null,
    2: ranks?.[2] ?? null,
    3: ranks?.[3] ?? null,
  };
  if (nid != null) {
    [1, 2, 3].forEach((slot) => {
      if (slot !== r && next[slot] === nid) next[slot] = null;
    });
  }
  next[r] = nid;
  return next;
}

/**
 * Clear rank slot `rank` and COMPACT higher ranks down so the sequence
 * stays dense (clearing Primary while Secondary is set slides Secondary up
 * into Primary). Gaps don't crash the color algorithm but read as a bug to
 * the user, so we keep ranks gap-free. Returns a new `{1,2,3}` map.
 */
export function clearThemeAtRank(ranks, rank) {
  const r = Number(rank);
  if (!Number.isFinite(r) || r < 1 || r > 3) return ranks;
  const ordered = [ranks?.[1] ?? null, ranks?.[2] ?? null, ranks?.[3] ?? null];
  ordered[r - 1] = null;
  const compacted = ordered.filter((x) => x != null);
  return {
    1: compacted[0] ?? null,
    2: compacted[1] ?? null,
    3: compacted[2] ?? null,
  };
}

/**
 * Serialize a picker selection into the canonical wire payload the
 * backend expects on BOTH create (submitDynDataNU.php) and edit
 * (EDRDynamic.php). Extracted from useWizardSubmit so the wizard and the
 * ContributionComp create/edit forms emit byte-identical payloads.
 *
 * `selection`:
 *   - `themeRanks`        `{1,2,3}` map of theme ids (or null) — ranked.
 *   - `propertyTypeRanks` `{1,2,3}` map of property-type ids — ranked
 *                         (client July 2026 #7). OPTIONAL.
 *   - `communityRanks`    `{1,2,3}` map of community ids — ranked
 *                         (client July 2026 #7). OPTIONAL.
 *   - `propertyTypeId`    LEGACY single id or null (pre-#7 single-select).
 *                         Folded into the id set as a plain assignment;
 *                         kept so any un-migrated caller keeps working.
 *   - `communityId`       LEGACY single id or null. Same treatment.
 *   - `taxonomyIds`       OPTIONAL legacy flat array (import-suggested-chips
 *                         / tests). Folded into the id set so it still
 *                         propagates — Codex eng-review insisted this
 *                         fallback survive the extraction.
 *
 * Returns:
 *   - `taxonomyIds`   deduped number[] — the full assigned set (all kinds +
 *                     any legacy ids). Source of truth for "what's
 *                     assigned"; the backend treats this as canonical.
 *   - `taxonomyRanks` `[{ rank, id }]` — the Primary/Secondary/Tertiary
 *                     overlay for EVERY ranked kind (themes + property type
 *                     + communities). The backend stores `rank` per
 *                     (shape, taxonomy) generically, so ranked PT/community
 *                     persist with no backend change. Backends that don't
 *                     understand ranks ignore it and default rank=1.
 */
export function buildTaxonomyPayload(selection) {
  const {
    themeRanks,
    propertyTypeRanks,
    communityRanks,
    propertyTypeId,
    communityId,
    taxonomyIds,
  } = selection || {};

  // Serialize one {1,2,3} rank map into ordered [{rank,id}] entries,
  // dropping empty slots and non-positive ids.
  const ranksFrom = (map) =>
    [1, 2, 3]
      .map((r) => ({ rank: r, id: (map || {})[r] }))
      .filter((e) => e.id != null)
      .map((e) => ({ rank: e.rank, id: Number(e.id) }))
      .filter((e) => Number.isFinite(e.id) && e.id > 0);

  const taxonomyRanks = [
    ...ranksFrom(themeRanks),
    ...ranksFrom(propertyTypeRanks),
    ...ranksFrom(communityRanks),
  ];

  const idSet = new Set();
  taxonomyRanks.forEach((e) => idSet.add(e.id));
  const addSingle = (raw) => {
    if (raw == null) return;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) idSet.add(n);
  };
  // Legacy single-select ids (pre-#7): assignment only, no rank overlay,
  // so byte-compat with callers that still pass propertyTypeId/communityId.
  addSingle(propertyTypeId);
  addSingle(communityId);
  (Array.isArray(taxonomyIds) ? taxonomyIds : []).forEach(addSingle);

  return { taxonomyIds: Array.from(idSet), taxonomyRanks };
}

/**
 * Build an O(1) `id → taxonomy` map from a flat catalogue. Cheap to
 * recompute per render but most callers will memoize it once per
 * catalogue change in FilterContext.
 */
export function indexTaxonomiesById(taxonomies) {
  const out = new Map();
  for (const t of taxonomies || []) {
    if (t && t.id != null) out.set(Number(t.id), t);
  }
  return out;
}

/**
 * Pick the marker / polygon paint color for a shape based on the chips
 * currently assigned to it.
 *
 * Active-lens model (catalogue v3, June 10 2026): the map paints through
 * ONE lens at a time — the active taxonomy kind (Theme / Property type /
 * Communities). `activeKind` restricts the candidate pool to chips of
 * that kind, so switching the lens repaints the map by a different
 * dimension. Within the Themes lens, chips are ranked Primary (1) /
 * Secondary (2) / Tertiary (3) per story and the lowest-rank selected
 * chip wins; the other two kinds are single-select per story (D4) so
 * their lone chip always wins.
 *
 * Strategy:
 *   - No chips on the shape → `defaultColor` (preserves legacy palette).
 *   - With chips:
 *     1. Build the candidate set: chips of `activeKind` only (when a
 *        kind is given), further intersected with `activeIds` when the
 *        active lens has a selection; otherwise every active-kind chip
 *        on the shape.
 *     2. Rank them — themes pull a 1-3 rank from `themeRanks`; the
 *        unranked kinds synthesize a sentinel so id-order tiebreaks.
 *     3. Sort by (rank ASC, id ASC). The id tiebreaker keeps the
 *        choice stable across reloads when two chips share a rank.
 *     4. Return the first valid color; defaultColor if none (e.g. the
 *        shape carries no chip of the active kind).
 *
 * `themeRanks` is the per-shape `{ taxonomyId: rank }` map the backend
 * emits next to `taxonomy_ids` (selectProjectCont.php → migration 023).
 * It's optional: when omitted (e.g. pre-migration data or call sites
 * that don't have a per-shape map yet) every chip is treated as
 * rank=1 and we fall back to first-match-in-id-order behavior, which
 * exactly matches the v1 algorithm.
 *
 * `activeIds` (optional) restricts the candidate pool to chips the
 * user has filtered ON within the active lens. Without a selection the
 * function paints by the shape's active-kind chip so the lens still
 * color-codes the map before any chip is picked.
 *
 * `activeKind` (optional) scopes candidates to a single kind. When
 * omitted the function considers every chip on the shape (legacy
 * cross-kind behavior, retained for callers that predate the lens).
 */
export function pickPrimaryChipColor(
  shapeTaxonomyIds,
  taxonomyIndex,
  defaultColor,
  activeIds = null,
  themeRanks = null,
  activeKind = null,
) {
  if (!Array.isArray(shapeTaxonomyIds) || shapeTaxonomyIds.length === 0) {
    return defaultColor;
  }
  const restrict = activeIds && activeIds.size > 0;

  // Build candidates: (id, taxonomy, rank). Skip chips outside the active
  // lens, skip non-selected when the lens is filtered, and skip ids the
  // catalogue doesn't know about (stale junction rows for deleted chips —
  // they should never paint).
  const candidates = [];
  for (const id of shapeTaxonomyIds) {
    const numId = Number(id);
    if (restrict && !activeIds.has(numId)) continue;
    const t = taxonomyIndex.get(numId);
    if (!t || !t.color) continue;
    if (activeKind && t.kind !== activeKind) continue;
    // Catch-all buckets ("Other"/"Unspecified") never paint a hue on the
    // map — they mean "unassigned / doesn't fit", so a shape whose only
    // active-kind chip is catch-all falls through to defaultColor
    // (greyscale). Flag-authoritative-when-present, name fallback
    // otherwise. (Chip pills keep their catalogue color; this is map-only.)
    if (isCatchAllTaxonomy(t)) continue;
    // Effective rank: ranked kinds pull from the per-shape rank overlay
    // (themeRanks — keyed by ALL taxonomy ids since selectProjectCont
    // emits ranks for every kind). Client July 2026 (#7) made Property
    // Type and Communities ranked too, so the lowest-rank selected chip
    // wins within any active lens. Any non-ranked kind (future) would
    // synthesize a sentinel so a multi-chip shape still resolves
    // deterministically by id order.
    let rank;
    if (kindIsRanked(t.kind)) {
      const r = themeRanks ? Number(themeRanks[numId] ?? themeRanks[String(numId)]) : NaN;
      rank = Number.isFinite(r) && r > 0 ? r : 1;
    } else {
      rank = 1000; // synthetic; unranked kinds tiebreak by id
    }
    candidates.push({ id: numId, color: t.color, rank });
  }

  if (candidates.length === 0) return defaultColor;

  // Sort by (rank ASC, id ASC). Stable tiebreak prevents flicker
  // when two chips share the same rank (e.g. legacy multi-theme
  // posts where backfill assigned identical ranks).
  candidates.sort((a, b) => (a.rank - b.rank) || (a.id - b.id));
  return candidates[0].color;
}

/**
 * "Does this shape pass the active-lens filter?"
 *
 * Active-lens model (catalogue v3): only the currently active taxonomy
 * kind filters the map. `activeSelectedIds` is that one lens's selected
 * chip set (e.g. the Communities set when the Communities lens is on).
 *
 *   - Empty selection → everything passes. The lens recolors the map but
 *     hides nothing until the user picks at least one chip.
 *   - Otherwise the shape passes if it carries at least one chip that is
 *     both selected AND of the active kind (any-of within the lens).
 *
 * Selections in the inactive lenses are deliberately ignored here — they
 * persist in their own Sets so switching the lens is a pure view change,
 * but they don't filter until their lens is the active one.
 *
 * `activeSelectedIds` only ever holds ids of the active kind, so the
 * `activeKind` re-check against the catalogue is belt-and-suspenders: it
 * stops a stale junction row (a chip since re-kinded or deleted) from
 * sneaking a shape through the filter.
 *
 * Catch-all ("Other") semantics — client feedback #4: the "Other" chip
 * means "unassigned in this lens", so selecting it must also reveal
 * shapes that carry NO chip of the active kind (not only shapes tagged
 * with the literal "Other" chip). That makes "Other + every specific
 * chip" behave like "All" (show everything). A specific-only selection
 * still hides the unassigned shapes.
 */
export function shapeMatchesActiveLens(
  shapeTaxonomyIds,
  activeSelectedIds,
  taxonomyIndex,
  activeKind = null,
) {
  if (!activeSelectedIds || activeSelectedIds.size === 0) return true;

  // Is a catch-all chip of the active kind currently selected? If so, an
  // unassigned-in-lens shape (no active-kind chip) should pass.
  let catchAllSelected = false;
  for (const id of activeSelectedIds) {
    const t = taxonomyIndex.get(Number(id));
    if (
      t &&
      (!activeKind || t.kind === activeKind) &&
      isCatchAllTaxonomy(t)
    ) {
      catchAllSelected = true;
      break;
    }
  }

  const ids = Array.isArray(shapeTaxonomyIds) ? shapeTaxonomyIds : [];
  const hasActiveKindChip = ids.some((id) => {
    const t = taxonomyIndex.get(Number(id));
    return t && (!activeKind || t.kind === activeKind);
  });

  // Unassigned in this lens: matches iff the catch-all chip is selected.
  if (!hasActiveKindChip) return catchAllSelected;

  // Assigned: passes if it carries a selected chip of the active kind.
  for (const id of ids) {
    const numId = Number(id);
    if (!activeSelectedIds.has(numId)) continue;
    const t = taxonomyIndex.get(numId);
    if (t && (!activeKind || t.kind === activeKind)) return true;
  }
  return false;
}
